import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowLeft, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Public browse page for Fliboe Flip — indie audiobook catalog.
 * Route: /flip (or /chapters legacy). Anyone can land here, browse, and click through to
 * individual book pages at /book/:slug.
 */
export default function ChaptersCatalog() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/books");
        setBooks(data || []);
      } catch (_) { setBooks([]); }
      finally { setLoading(false); }
    })();
  }, []);

  // Derive category list from what's actually published
  const cats = ["all", ...Array.from(new Set(books.map(b => b.category).filter(Boolean)))];
  const filtered = category === "all" ? books : books.filter(b => b.category === category);

  return (
    <main className="relative min-h-screen" data-testid="chapters-catalog">
      <div className="absolute inset-0 pointer-events-none -z-10">
        <img src="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/60 via-black/85 to-[#050505]" />
      </div>
      <div className="max-w-5xl mx-auto px-5 py-12">
      <Link to="/" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-emerald-500 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="w-6 h-6 text-violet-400" />
        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-violet-400">// FLIBOE FLIP</div>
      </div>
      <h1 className="fl-display text-4xl sm:text-5xl font-black text-white tracking-tighter">Indie audiobooks. Zero gatekeepers.</h1>
      <p className="text-slate-400 mt-3 max-w-2xl leading-relaxed">
        AI-narrated audiobooks in 16 languages, from independent authors who keep 100% of every tip.
        Free first chapters on every book. $9.99/month unlocks the full library.
      </p>

      {cats.length > 1 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="catalog-category-filter">
          {cats.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${
                category === c ? "bg-violet-500 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
              }`}
              data-testid={`catalog-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="mt-10">
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="fl-card p-10 text-center">
            <Sparkles className="w-8 h-8 text-violet-400 mx-auto mb-3" />
            <div className="text-white font-bold text-lg">The catalog opens soon</div>
            <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
              We&apos;re onboarding the first wave of indie authors right now. Check back in a few days — or&nbsp;
              <Link to="/publish" className="text-emerald-400 hover:underline">submit your book</Link> to be next.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5" data-testid="chapters-catalog-grid">
            {filtered.map(b => (
              <div key={b.book_id} className="fl-card overflow-hidden hover:ring-2 hover:ring-violet-500 transition" data-testid={`catalog-book-${b.book_id}`}>
                <Link to={`/book/${b.slug}`}>
                  {b.cover_url ? (
                    <img src={b.cover_url} alt="" className="w-full aspect-[3/4] object-cover" />
                  ) : (
                    <div className="w-full aspect-[3/4] bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-white/40" />
                    </div>
                  )}
                </Link>
                <div className="p-3">
                  <Link to={`/book/${b.slug}`} className="text-white font-bold text-sm line-clamp-2 hover:text-violet-300">{b.title}</Link>
                  <div className="text-slate-400 text-xs mt-0.5 truncate">
                    by{" "}
                    {b.author_slug ? (
                      <Link to={`/author/${b.author_slug}`} className="text-violet-300 hover:underline" data-testid={`catalog-author-${b.book_id}`}>{b.author_name}</Link>
                    ) : (
                      <span>{b.author_name}</span>
                    )}
                  </div>
                  <div className="text-violet-300 text-[10px] mt-1 uppercase tracking-widest">{b.chapter_count} chapters · {b.language}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </main>
  );
}
